import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

export const EASE = [0.16, 1, 0.3, 1];

export const Reveal = ({ children, delay = 0, y = 40, className = "", ...rest }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-12% 0px" }}
    transition={{ duration: 0.9, delay, ease: EASE }}
    {...rest}
  >
    {children}
  </motion.div>
);

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({ y: 0, transition: { duration: 1, delay: i.delay + i.index * i.stagger, ease: EASE } }),
};

export const SplitLines = ({ lines, className = "", lineClassName = "", delay = 0, stagger = 0.11, onMount = false }) => (
  <motion.span
    className={`block ${className}`}
    initial="hidden"
    {...(onMount ? { animate: "visible" } : { whileInView: "visible", viewport: { once: true, margin: "-10% 0px" } })}
  >
    {lines.map((line, i) => (
      <span key={`line-${i}-${typeof line === "string" ? line : "node"}`} className="block overflow-hidden pb-[0.06em] -mb-[0.06em]">
        <motion.span className={`block ${lineClassName}`} variants={lineVariants} custom={{ delay, index: i, stagger }}>
          {line}
        </motion.span>
      </span>
    ))}
  </motion.span>
);

export const Counter = ({ value, prefix = "", suffix = "", decimals = 0, duration = 1500 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start;
    let raf;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / duration, 1);
      setV(value * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);
  return (
    <span ref={ref}>
      {prefix}
      {v.toFixed(decimals)}
      {suffix}
    </span>
  );
};

export const SectionHeader = ({ eyebrow, lines, right, id }) => (
  <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 sm:mb-16">
    <div>
      <Reveal y={16} className="eyebrow mb-4" data-testid={id ? `${id}-eyebrow` : undefined}>
        {eyebrow}
      </Reveal>
      <h2 className="h-display text-4xl sm:text-5xl lg:text-6xl text-white">
        <SplitLines lines={lines} />
      </h2>
    </div>
    {right && (
      <Reveal delay={0.2} className="max-w-md text-slate-400 text-sm sm:text-base leading-relaxed lg:text-right">
        {right}
      </Reveal>
    )}
  </div>
);
